import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Plus, HandCoins, Play, FileText, Download, Printer, Check } from "lucide-react";
import { API } from "@/lib/api";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

const PAYROLL_VALID_TABS = ['payroll', 'kasbon'];

export default function Payroll() {
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const [tab, setTabState] = useState(
    PAYROLL_VALID_TABS.includes(requestedTab) ? requestedTab : 'payroll'
  );
  useEffect(() => {
    const requested = searchParams.get("tab");
    if (PAYROLL_VALID_TABS.includes(requested)) setTabState(requested);
  }, [searchParams]);
  const setTab = (value) => {
    if (!PAYROLL_VALID_TABS.includes(value)) return;
    setTabState(value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  };
  return (
    <div className="page-shell" data-layered-navigation="payroll">
      <div data-workspace-redundant-header>
        <h1 className="text-2xl font-extrabold font-display flex items-center gap-2"><Wallet className="w-6 h-6 text-orange-500"/>Payroll & Kasbon</h1>
        <p className="text-sm text-slate-500">Slip gaji bulanan, otomatis potong kasbon dan jurnal keuangan</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-sticky-page-tabs="payroll">
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Wallet className="w-4 h-4 mr-1"/>Payroll Run</TabsTrigger>
          <TabsTrigger value="kasbon" data-testid="tab-kasbon"><HandCoins className="w-4 h-4 mr-1"/>Kasbon</TabsTrigger>
        </TabsList>
        <TabsContent value="payroll"><PayrollTab/></TabsContent>
        <TabsContent value="kasbon"><KasbonTab/></TabsContent>
      </Tabs>
    </div>
  );
}

function PayrollTab() {
  const [runs, setRuns] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openRun, setOpenRun] = useState(false);
  const period = new Date().toISOString().slice(0, 7);
  const [form, setForm] = useState({ period, business_unit_id: "", outlet_id: "" });

  const load = () => api.get("/payroll").then(({ data }) => setRuns(data));
  useEffect(() => {
    load();
    api.get("/business-units").then(({ data }) => setBusinessUnits(data));
    api.get("/outlets").then(({ data }) => setOutlets(data));
  }, []);

  async function run() {
    try {
      const { data } = await api.post("/payroll/run", {
        period: form.period,
        business_unit_id: form.business_unit_id || null,
        outlet_id: form.outlet_id || null,
      });
      toast.success(`Draft payroll ${data.period} dibuat (${data.lines.length} karyawan)`);
      setOpenRun(false); load(); setSelected(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  async function approve() {
    if (!selected) return;
    await api.post(`/payroll/${selected.id}/approve`);
    toast.success("Payroll disetujui + jurnal otomatis dipost");
    const { data } = await api.get(`/payroll/${selected.id}`);
    setSelected(data); load();
  }
  async function markPaid() {
    if (!selected) return;
    await api.post(`/payroll/${selected.id}/paid`);
    toast.success("Payroll ditandai LUNAS");
    const { data } = await api.get(`/payroll/${selected.id}`);
    setSelected(data); load();
  }

  function downloadCSV() {
    if (!selected) return;
    const token = localStorage.getItem("mazboy_token");
    fetch(`${API}/payroll/${selected.id}/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = `payroll-${selected.period}.csv`; a.click();
      });
  }

  function printSlip(line) {
    const w = window.open("", "_blank", "width=400,height=600");
    const html = `<html><head><title>Slip Gaji</title>
      <style>body{font-family:monospace;padding:20px;font-size:12px;} h2{margin:4px 0;} table{width:100%;border-collapse:collapse} td{padding:2px 0} .r{text-align:right}</style></head><body>
      <h2 style="text-align:center">SLIP GAJI</h2>
      <div style="text-align:center;font-size:11px;">Periode ${selected.period}</div>
      <hr/>
      <table>
        <tr><td>Nama</td><td class="r">${line.employee_name}</td></tr>
        <tr><td>Tipe</td><td class="r">${line.employment_type}</td></tr>
        ${line.days ? `<tr><td>Hari kerja</td><td class="r">${line.days}</td></tr>` : ""}
      </table>
      <hr/>
      <table>
        <tr><td>Gaji Pokok</td><td class="r">${rupiah(line.base)}</td></tr>
        <tr><td>Komisi</td><td class="r">${rupiah(line.commission)}</td></tr>
        <tr><td>Lembur</td><td class="r">${rupiah(line.overtime)}</td></tr>
        <tr><td><b>Gross</b></td><td class="r"><b>${rupiah(line.gross)}</b></td></tr>
        <tr><td>Potongan Kasbon</td><td class="r">- ${rupiah(line.kasbon_deduction)}</td></tr>
        <tr><td>Potongan Lain</td><td class="r">- ${rupiah(line.other_deduction)}</td></tr>
      </table>
      <hr/>
      <table><tr><td><b>NET</b></td><td class="r"><b>${rupiah(line.net)}</b></td></tr></table>
      <hr/>
      <div style="text-align:center;font-size:10px;">MAZBOY HUB · ${new Date().toLocaleString("id-ID")}</div>
      <script>window.onload=()=>window.print()</script>
      </body></html>`;
    w.document.write(html); w.document.close();
  }

  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="p-4 lg:col-span-1">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">Payroll Runs</div>
          <Button data-testid="payroll-new-btn" size="sm" onClick={()=>setOpenRun(true)} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Run</Button>
        </div>
        <div className="space-y-2 max-h-[600px] overflow-auto">
          {runs.map(r => (
            <button data-testid={`payroll-item-${r.id}`} key={r.id} onClick={async ()=>{const { data } = await api.get(`/payroll/${r.id}`); setSelected(data);}}
              className={`w-full text-left p-3 rounded-lg border transition ${selected?.id===r.id ? "border-orange-500 bg-orange-50":"bg-white hover:bg-slate-50"}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium">{r.period}</div>
                <Badge className={r.status==="paid"?"bg-emerald-500":r.status==="approved"?"bg-blue-500":"bg-slate-400"}>{r.status}</Badge>
              </div>
              <div className="text-xs text-slate-500 num">{r.lines?.length || 0} karyawan · Net {rupiah(r.total_net)}</div>
            </button>
          ))}
          {!runs.length && <div className="text-sm text-slate-400 py-4 text-center">Belum ada payroll</div>}
        </div>
      </Card>

      <Card className="p-4 lg:col-span-2">
        {!selected ? <div className="text-center text-slate-400 py-16">Pilih payroll dari kiri, atau klik "Run" untuk membuat baru</div> : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-lg font-bold">Payroll {selected.period}</div>
                <div className="text-xs text-slate-500">{selected.lines?.length || 0} karyawan · Status: <Badge className={selected.status==="paid"?"bg-emerald-500":selected.status==="approved"?"bg-blue-500":"bg-slate-400"}>{selected.status}</Badge></div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={downloadCSV}><Download className="w-3.5 h-3.5 mr-1"/>CSV</Button>
                {selected.status === "draft" && <Button data-testid="payroll-approve-btn" onClick={approve} className="bg-blue-500 hover:bg-blue-600"><Check className="w-4 h-4 mr-1"/>Setujui</Button>}
                {selected.status === "approved" && <Button data-testid="payroll-paid-btn" onClick={markPaid} className="bg-emerald-500 hover:bg-emerald-600"><Check className="w-4 h-4 mr-1"/>Tandai Lunas</Button>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="p-3 rounded bg-slate-50"><div className="text-xs text-slate-500">Total Gross</div><div className="font-bold num">{rupiah(selected.total_gross)}</div></div>
              <div className="p-3 rounded bg-slate-50"><div className="text-xs text-slate-500">Total Kasbon</div><div className="font-bold num text-rose-600">- {rupiah(selected.total_kasbon)}</div></div>
              <div className="p-3 rounded bg-emerald-50"><div className="text-xs text-emerald-700">Total Net</div><div className="font-bold num text-emerald-700">{rupiah(selected.total_net)}</div></div>
            </div>
            <div className="max-h-[400px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b sticky top-0 bg-white">
                  <tr><th className="text-left py-2">Karyawan</th><th className="text-right">Base</th><th className="text-right">Kasbon</th><th className="text-right">Net</th><th></th></tr>
                </thead>
                <tbody>
                  {selected.lines?.map(l => (
                    <tr key={l.employee_id} className="border-b">
                      <td className="py-2 font-medium">{l.employee_name}</td>
                      <td className="text-right num">{rupiah(l.base)}</td>
                      <td className="text-right num text-rose-600">{l.kasbon_deduction>0 ? `- ${rupiah(l.kasbon_deduction)}` : "-"}</td>
                      <td className="text-right num font-bold">{rupiah(l.net)}</td>
                      <td className="text-right"><Button size="sm" variant="outline" onClick={()=>printSlip(l)}><Printer className="w-3 h-3"/></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      <Dialog open={openRun} onOpenChange={setOpenRun}>
        <DialogContent>
          <DialogHeader><DialogTitle>Run Payroll Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs">Periode (YYYY-MM)</label><Input data-testid="payroll-period-input" value={form.period} onChange={e=>setForm({...form,period:e.target.value})}/></div>
            <div><label className="text-xs">Business Unit (opsional)</label>
              <Select value={form.business_unit_id || "all"} onValueChange={v=>setForm({...form,business_unit_id: v==="all"?"":v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua BU</SelectItem>{businessUnits.map(b=><SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs">Outlet (opsional)</label>
              <Select value={form.outlet_id || "all"} onValueChange={v=>setForm({...form,outlet_id: v==="all"?"":v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua outlet</SelectItem>{outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.brand} · {o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpenRun(false)}>Batal</Button><Button data-testid="payroll-run-btn" onClick={run} className="bg-orange-500 hover:bg-orange-600"><Play className="w-4 h-4 mr-1"/>Run</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KasbonTab() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", amount: 0, installments: 1, note: "" });

  const load = () => api.get("/kasbon").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/employees").then(({ data }) => setEmployees(data)); }, []);

  async function save() {
    if (!form.employee_id || form.amount <= 0) return toast.error("Lengkapi form");
    await api.post("/kasbon", form);
    toast.success("Kasbon diajukan");
    setOpen(false); setForm({ employee_id: "", amount: 0, installments: 1, note: "" });
    load();
  }
  async function approve(k) { await api.post(`/kasbon/${k.id}/approve`); toast.success("Kasbon disetujui + jurnal dipost"); load(); }
  async function reject(k) { await api.post(`/kasbon/${k.id}/reject`); toast.success("Kasbon ditolak"); load(); }

  return (
    <div className="mt-4 space-y-4">
      <Button data-testid="kasbon-new-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Ajukan Kasbon</Button>
      <Card className="p-4">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 border-b">
            <tr><th className="text-left py-2">Karyawan</th><th className="text-right">Jumlah</th><th className="text-right">Cicilan</th><th className="text-right">Sisa</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(k => (
              <tr key={k.id} className="border-b">
                <td className="py-2 font-medium">{k.employee_name}</td>
                <td className="text-right num">{rupiah(k.amount)}</td>
                <td className="text-right num">{k.paid_installments}/{k.installments} · {rupiah(k.installment_amount)}</td>
                <td className="text-right num">{rupiah(k.outstanding)}</td>
                <td><Badge className={k.status==="paid"?"bg-emerald-500":k.status==="approved"?"bg-blue-500":k.status==="rejected"?"bg-rose-500":"bg-amber-500"}>{k.status}</Badge></td>
                <td className="text-right space-x-1">
                  {k.status === "pending" && <>
                    <Button size="sm" onClick={()=>approve(k)} className="bg-emerald-500 hover:bg-emerald-600">Approve</Button>
                    <Button size="sm" variant="outline" onClick={()=>reject(k)} className="text-rose-600">Reject</Button>
                  </>}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="6" className="py-8 text-center text-slate-400">Belum ada kasbon</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajukan Kasbon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs">Karyawan</label>
              <Select value={form.employee_id} onValueChange={v=>setForm({...form,employee_id:v})}>
                <SelectTrigger data-testid="kasbon-emp-select"><SelectValue placeholder="Pilih karyawan"/></SelectTrigger>
                <SelectContent>{employees.map(e=><SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs">Jumlah Kasbon</label><Input data-testid="kasbon-amount-input" type="number" value={form.amount} onChange={e=>setForm({...form,amount:Number(e.target.value)})}/></div>
            <div><label className="text-xs">Jumlah Cicilan (bulanan)</label><Input data-testid="kasbon-installments-input" type="number" value={form.installments} onChange={e=>setForm({...form,installments:Number(e.target.value)})}/></div>
            <div><label className="text-xs">Catatan</label><Input value={form.note} onChange={e=>setForm({...form,note:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="kasbon-save-btn" onClick={save} className="bg-orange-500 hover:bg-orange-600">Ajukan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
